import { useMutation } from '@apollo/client/react'
import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ChatThread } from '@src/components/sco/Chat/ChatThread'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { ENSURE_SCO_PITCH_DECK_SLIDE_CHAT_MUTATION } from '@src/components/sco/PitchDeckAuditor/gql/operations'
import {
  PITCH_DECK_UPGRADE_PRODUCT_ID,
  usePitchDeckSlideAccess,
} from '@src/components/sco/PitchDeckAuditor/model/access'
import { PitchDeckView } from '@src/components/sco/PitchDeckAuditor/model/types'
import { gql } from '@src/lib/gql'
import { useEffect, useState } from 'react'
import { LuArrowRight, LuSparkles } from 'react-icons/lu'

const UPSERT_CHAT_COMMENT = gql`
  mutation UpsertPitchDeckChatComment($input: UpsertChatCommentInput!) {
    upsertChatComment(input: $input) {
      comment {
        id
      }
    }
  }
`

const DECK_PROMPTS = [
  'What should I fix first?',
  'Which slides are dragging my score down?',
  'Is this deck ready to send to investors?',
]

const SLIDE_PROMPTS = [
  'Why did this slide score the way it did?',
  'Rewrite this slide for me',
  'What would an investor object to here?',
]

export function PitchDeckChatPanel({
  deck,
  activeSlideId,
}: {
  deck: PitchDeckView
  activeSlideId: string | null
}) {
  const activeSlide = activeSlideId ? deck.slides.find(s => s.pitchDeckSlideId === activeSlideId) : null
  const { isSlideLocked } = usePitchDeckSlideAccess()
  // Chatting about a slide is gated the same way as reading it: slides beyond the free preview
  // require `canAccessPitchDeckAuditor`, otherwise Pitchy would hand over the locked analysis.
  const lockedSlide = activeSlide && isSlideLocked(activeSlide) ? activeSlide : null
  const [ensuredThreadId, setEnsuredThreadId] = useState<string | null>(null)
  const [ensureChat] = useMutation(ENSURE_SCO_PITCH_DECK_SLIDE_CHAT_MUTATION)
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(UPSERT_CHAT_COMMENT)

  const baseThreadId = activeSlide ? activeSlide.threadId : deck.deckThreadId
  const threadId = baseThreadId || ensuredThreadId

  // Reset the ensure cache when switching slides / back to the deck.
  useEffect(() => {
    setEnsuredThreadId(null)
  }, [activeSlideId])

  // Safety net: classify creates per-slide sessions eagerly, but ensure one if missing.
  useEffect(() => {
    if (activeSlide && !lockedSlide && !activeSlide.threadId && !ensuredThreadId) {
      ensureChat({ variables: { input: { pitchDeckSlideId: activeSlide.pitchDeckSlideId } } })
        .then(res => {
          const t = (res.data as any)?.ensureScoPitchDeckSlideChat?.pitchDeckSlide?.toolSession?.threadId
          if (t) {
            setEnsuredThreadId(t)
          }
        })
        .catch(() => undefined)
    }
  }, [activeSlide?.pitchDeckSlideId, activeSlide?.threadId, ensuredThreadId, ensureChat])

  const statusLine = activeSlide
    ? `Discussing Slide ${activeSlide.position} · ${activeSlide.name}`
    : 'Discussing your full deck audit'
  const prompts = activeSlide ? SLIDE_PROMPTS : DECK_PROMPTS

  const onStartClick = (body: string) => {
    if (!threadId) {
      return
    }
    upsertChatComment({ variables: { input: { body, threadId } } })
  }

  const header = (
    <HStack
      bgColor={'colorPalette.panel'}
      borderBottomWidth={'1px'}
      borderColor={'colorPalette.border/30'}
      flexShrink={0}
      gap={3}
      height={'56px'}
      padding={2}
    >
      <Avatar.Root colorPalette={'orange'} size={'sm'}>
        <Avatar.Fallback>
          <LuSparkles size={16} />
        </Avatar.Fallback>
      </Avatar.Root>
      <Box flex={1} minWidth={0}>
        <Text fontSize={'sm'} fontWeight={'semibold'}>
          Pitch Deck Coach
        </Text>
        <HStack gap={1.5}>
          <Box bg={'green.solid'} borderRadius={'full'} boxSize={1.5} />
          <Text color={'fg.muted'} fontSize={'2xs'} lineClamp={1}>
            {statusLine}
          </Text>
        </HStack>
      </Box>
    </HStack>
  )

  const blankState = (
    <VStack align={'stretch'} gap={4} padding={6} width={'full'}>
      <VStack align={'stretch'} gap={1}>
        <Heading size={'md'}>Pitch Deck Coach</Heading>
        <Text color={'fg.muted'} fontSize={'sm'}>
          {activeSlide
            ? `Dig into Slide ${activeSlide.position} — ${activeSlide.name}. We will not be polite about your TAM.`
            : 'Argue with the audit, ask for rewrites, or get your fixes prioritized.'}
        </Text>
      </VStack>
      <ButtonGroup attached orientation={'vertical'} size={'sm'} variant={'outline'}>
        {prompts.map((prompt, idx) => (
          <Button
            key={idx}
            justifyContent={'space-between'}
            loading={upsertChatCommentMutation.loading}
            width={'full'}
            onClick={() => onStartClick(prompt)}
          >
            {prompt}
            <LuArrowRight />
          </Button>
        ))}
      </ButtonGroup>
    </VStack>
  )

  return (
    <VStack align={'stretch'} gap={0} width={'full'}>
      {header}
      <Flex direction={'column'} height={'full'} overflow={'hidden'} width={'full'}>
        {lockedSlide ? (
          <Box padding={4}>
            <PaywalledAction
              contentPermission={'canAccessPitchDeckAuditor'}
              title={`Slide ${lockedSlide.position} is part of the full audit. Upgrade to ask Pitch Deck Auditor about it.`}
              upgradeCtaLabel={'Unlock the Full Audit'}
              upgradeProductId={PITCH_DECK_UPGRADE_PRODUCT_ID}
              variant={'boxed'}
            />
          </Box>
        ) : threadId ? (
          <ChatThread
            bypassContentPermissions={true}
            chatMessageListProps={{ blankState, containerStackProps: { minHeight: 0 } }}
            hideHeader={true}
            // minHeight:0 lets the scrollable message list shrink within the flex column so it
            // scrolls internally instead of growing and pushing the composer past the clipped edge.
            threadId={threadId}
          />
        ) : (
          <Center flex={1} width={'full'}>
            <Spinner colorPalette={'orange'} />
          </Center>
        )}
      </Flex>
    </VStack>
  )
}
